import Phaser from 'phaser';
import { ASSET_H, ASSET_W, VIEWPORT_W } from '../constants';
import { scrollForPanel } from '../layout';
import { gameState } from '../state';

const PANEL_SWIPE_MS = 145;
const PANEL_SWIPE_MIN_MS = 60;
const PANEL_SWIPE_EASE = 'Power4.easeOut';

export function createPanelCamera(scene: Phaser.Scene) {
  const cam = scene.cameras.main;
  cam.setBounds(0, 0, ASSET_W, ASSET_H);
  cam.scrollX = scrollForPanel(gameState.currentPanel);

  let panGeneration = 0;
  let onScroll: ((scrollX: number) => void) | null = null;

  function snapScrollX(targetX: number) {
    cam.scrollX = targetX;
    onScroll?.(targetX);
  }

  function stopPan() {
    panGeneration += 1;
    scene.tweens.killTweensOf(cam);
  }

  function durationForDistance(targetX: number) {
    const dist = Math.abs(cam.scrollX - targetX);
    return Math.round(Math.max(PANEL_SWIPE_MIN_MS, Math.min(PANEL_SWIPE_MS, dist * 0.22)));
  }

  function panToScrollX(targetX: number) {
    stopPan();
    if (Math.abs(cam.scrollX - targetX) < 0.5) {
      snapScrollX(targetX);
      return Promise.resolve();
    }

    const duration = durationForDistance(targetX);
    const gen = panGeneration;
    return new Promise<void>((resolve) => {
      scene.tweens.add({
        targets: cam,
        scrollX: targetX,
        duration,
        ease: PANEL_SWIPE_EASE,
        onUpdate: () => {
          if (gen !== panGeneration) return;
          onScroll?.(cam.scrollX);
        },
        onComplete: () => {
          if (gen !== panGeneration) return;
          snapScrollX(targetX);
          resolve();
        },
      });
    });
  }

  function nearestPanel(scrollX = cam.scrollX) {
    return scrollX >= VIEWPORT_W * 0.5 ? 1 : 0;
  }

  return {
    setOnScroll(fn: (scrollX: number) => void) {
      onScroll = fn;
    },
    stopPan,
    scrollForPanel,
    nearestPanel,
    getScrollX: () => cam.scrollX,
    panToPanel(index: 0 | 1) {
      return panToScrollX(scrollForPanel(index));
    },
    dragBy(dx: number) {
      stopPan();
      const base = scrollForPanel(gameState.currentPanel);
      let next = base - dx;
      if (gameState.currentPanel === 0 && next < 0) {
        next *= 0.25;
      } else if (gameState.currentPanel === 1 && next > VIEWPORT_W) {
        next = VIEWPORT_W + (next - VIEWPORT_W) * 0.25;
      }
      cam.scrollX = Phaser.Math.Clamp(next, 0, VIEWPORT_W);
      onScroll?.(cam.scrollX);
    },
    setPanelInstant(index: 0 | 1) {
      stopPan();
      snapScrollX(scrollForPanel(index));
    },
  };
}
